import type { Metadata } from "next";

export const siteConfig = {
  name: "Mindtale",
  // Two descriptions, because they do two jobs.
  //
  // `description` is the fallback meta description: short enough to survive
  // Google's truncation, written for a person deciding whether to click.
  description:
    "Platformă de psihoterapie online din România. Îți recomandăm psihoterapeutul potrivit, iar sesiunea de cunoaștere este gratuită.",
  // `entityDescription` is what the Organization schema carries, and it is the
  // sentence a language model reaches for when it has to say what Mindtale is.
  // It names the two things that distinguish us — the assistant that does the
  // matching, and the free first session — because an assistant that cannot
  // find them on our own pages will describe us from a competitor's page
  // instead. (Observed: Google's AI Overview for "terapie online" sourced its
  // Mindtale line from hedepy.ro.) Keep it factual and self-contained: a
  // sentence that still makes sense lifted out of context is a sentence that
  // gets quoted.
  entityDescription:
    "Mindtale este o platformă de psihoterapie online din România. Un asistent, Zenyra, discută cu tine câteva minute și îți recomandă psihoterapeutul potrivit dintre specialiștii acreditați ai platformei. Prima sesiune de cunoaștere este gratuită. Mindtale pune deoparte 10% din profit pentru un fond care va finanța terapie pentru persoane care nu și-o pot permite.",
  url: "https://mindtale.ro",
  // JPG, not WebP: LinkedIn and some messengers do not render WebP previews.
  ogImage: "https://mindtale.ro/og-image.jpg",
  keywords: [
    "psiholog",
    "psiholog online",
    "psiholog online Romania",
    "terapie",
    "terapie online",
    "terapie online Romania",
    "terapeut",
    "terapeut online",
    "psihoterapeut",
    "psihoterapeut online",
    "psihoterapie",
    "psihoterapie online",
    "psihoterapie Romania",
    "consiliere psihologică",
    "consiliere online",
    "sănătate mintală",
    "sănătate emoțională",
    "sănătate mentală România",
    "terapie accesibilă",
    "platformă terapie",
    "ajutor psihologic online",
    "cabinet psihologie online",
    "sedinta terapie online",
    "găsește psiholog",
    "întreprindere socială",
    "acces la terapie",
  ],
  creator: "Mindtale",
  authors: [{ name: "Mindtale" }],
  // Legal identity for Organization schema. Only facts published on the site
  // go here; undefined fields are omitted from the markup. Fill telephone,
  // street address and CUI when you want them public (they strengthen the
  // entity Google builds for the brand on a YMYL site).
  legal: {
    legalName: "Zenyra Therapy SRL",
    email: "hello@mindtale.ro",
    telephone: undefined as string | undefined,
    streetAddress: undefined as string | undefined,
    addressLocality: undefined as string | undefined,
    postalCode: undefined as string | undefined,
    /** CUI. A verifiable registration number is one of the strongest signals
     *  Google has that the brand is a real, accountable business — which is
     *  what it looks for hardest on a health site. */
    taxID: "52778359" as string | undefined,
    /** D-U-N-S. The identifier Apple, Google and most business registries key
     *  a company on, so it ties this entity to the same company they already
     *  know about. */
    duns: "667824202" as string | undefined,
  },
  social: {
    facebook: "https://www.facebook.com/mindtale.ro",
    instagram: "https://www.instagram.com/mindtale.ro",
    linkedin: "https://linkedin.com/company/mindtale",
    tiktok: "https://www.tiktok.com/@mindtale.ro"

  },
};

export function constructMetadata({
  title = siteConfig.name,
  description = siteConfig.description,
  image = siteConfig.ogImage,
  keywords = siteConfig.keywords,
  noIndex = false,
  canonicalUrl,
}: {
  title?: string;
  description?: string;
  image?: string;
  keywords?: string[];
  noIndex?: boolean;
  canonicalUrl?: string;
} = {}): Metadata {
  return {
    metadataBase: new URL(siteConfig.url),
    // `absolute`, not `default`: Next applies the parent layout's template to
    // a child's `default` title, which rendered "... | Mindtale | Mindtale" on
    // every page. The template still applies to children that pass a plain
    // string (blog posts, therapist profiles).
    title: {
      absolute: title,
      template: `%s | ${siteConfig.name}`,
    },
    description,
    keywords: keywords.join(", "),
    authors: siteConfig.authors,
    creator: siteConfig.creator,
    openGraph: {
      type: "website",
      locale: "ro_RO",
      url: canonicalUrl || siteConfig.url,
      title,
      description,
      siteName: siteConfig.name,
      images: [
        {
          url: image,
          width: 1200,
          height: 630,
          alt: title,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      images: [image],
      creator: "@mindtale",
    },
    icons: {
      icon: [
        { url: "/favicon.ico", sizes: "any" },
        { url: "/icon-32.png", sizes: "32x32", type: "image/png" },
        { url: "/icon-192.png", sizes: "192x192", type: "image/png" },
        { url: "/icon-512.png", sizes: "512x512", type: "image/png" },
      ],
      shortcut: "/favicon.ico",
      apple: "/icon-180.png",
    },
    manifest: "/site.webmanifest",
    ...(canonicalUrl && {
      alternates: {
        canonical: canonicalUrl,
      },
    }),
    ...(noIndex && {
      robots: {
        index: false,
        follow: false,
      },
    }),
  };
}

const ORGANIZATION_ID = `${siteConfig.url}/#organization`;

function postalAddress() {
  const { streetAddress, addressLocality, postalCode } = siteConfig.legal;
  return {
    "@type": "PostalAddress",
    addressCountry: "RO",
    ...(streetAddress && { streetAddress }),
    ...(addressLocality && { addressLocality }),
    ...(postalCode && { postalCode }),
  };
}

export function generateOrganizationSchema() {
  const { legalName, email, telephone, taxID, duns } = siteConfig.legal;
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: "Mindtale",
    legalName,
    url: siteConfig.url,
    logo: `${siteConfig.url}/logo.svg`,
    // The long form here, not the meta one: this field is read by machines
    // describing the brand, so it should name what makes it distinct.
    description: siteConfig.entityDescription,
    email,
    ...(telephone && { telephone }),
    ...(taxID && { taxID }),
    ...(duns && { duns }),
    foundingDate: "2025-10-01",
    // schema.org lets `founder` repeat, and an array is how you say a company
    // has more than one. Both names are here so the entity Google builds for
    // Mindtale links to both people instead of attributing it to one.
    founder: [
      { "@type": "Person", name: "Paula Pricope" },
      { "@type": "Person", name: "Eduard Modreanu" },
    ],
    address: postalAddress(),
    areaServed: { "@type": "Country", name: "Romania" },
    knowsLanguage: ["ro"],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      email,
      ...(telephone && { telephone }),
      availableLanguage: "Romanian",
    },
    sameAs: Object.values(siteConfig.social),
  };
}

export function generateLocalBusinessSchema() {
  const { legalName, email, telephone } = siteConfig.legal;
  return {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": `${siteConfig.url}/#medicalbusiness`,
    name: "Mindtale",
    legalName,
    parentOrganization: { "@id": ORGANIZATION_ID },
    description: siteConfig.entityDescription,
    url: siteConfig.url,
    logo: `${siteConfig.url}/logo.svg`,
    email,
    ...(telephone && { telephone }),
    address: postalAddress(),
    /* No `priceRange`. It used to claim "150-300 RON" and point at /cat-costa
       for the band, but that page no longer publishes one, and it cannot:
       every therapist sets their own fee, so there is no platform price to
       state. An unsourced band in structured data is the same defect as an
       unsourced number in the copy, only harder to notice. */
    currenciesAccepted: "RON",
    areaServed: {
      "@type": "Country",
      name: "Romania",
    },
    medicalSpecialty: "MentalHealth",
    serviceType: "Online Psychotherapy",
  };
}

export function generateWebSiteSchema() {
  // No potentialAction: the site has no search endpoint, and Google retired
  // the sitelinks search box in 2024.
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteConfig.name,
    url: siteConfig.url,
    inLanguage: "ro",
    publisher: { "@id": ORGANIZATION_ID },
  };
}

export function generateWebPageSchema(title: string, description: string, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url,
    isPartOf: {
      "@type": "WebSite",
      url: siteConfig.url,
    },
    publisher: {
      "@type": "Organization",
      name: "Mindtale",
      logo: {
        "@type": "ImageObject",
        url: `${siteConfig.url}/logo.svg`,
      },
    },
  };
}
